import { CommonModule } from '@angular/common';
import { Component, computed, inject, resource } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MAT_DATE_LOCALE, MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { MatToolbarModule } from '@angular/material/toolbar';
import { debounceTime, distinctUntilChanged, startWith, switchMap } from 'rxjs';

import type { IOrganization } from '#backend/common/organization-types';
import { toSignal } from '@angular/core/rxjs-interop';
import { provideLuxonDateAdapter } from '@angular/material-luxon-adapter';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { DateTime } from 'luxon';
import { OrganizationService } from '../../../shared/services/organization.service';
import { SmartOrpcClientService } from '../../../shared/services/smart-orpc-client.service';
import { TypeSafeMatCellDef } from '../../../type-safe-mat-cell-def.directive';
import { SnackbarService } from '../../../shared/services/snackbar.service';
import { formatCNPJ } from '@brazilian-utils/brazilian-utils';
import type { contractOutput } from '#backend/contract';

interface SummaryRow {
  indicator: string;
  value: number | string;
  unit?: string;
}

@Component({
  selector: 'app-internal-summary',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatCardModule,
    MatChipsModule,
    MatDatepickerModule,
    MatDividerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatNativeDateModule,
    MatProgressBarModule,
    MatSelectModule,
    MatTableModule,
    MatToolbarModule,
    TypeSafeMatCellDef,
  ],
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'pt-BR' },
    provideLuxonDateAdapter(),
  ],
  templateUrl: './internal-summary.component.html',
  styleUrl: './internal-summary.component.scss',
})
export class InternalSummaryComponent {
  private orpc = inject(SmartOrpcClientService);
  private organizationService = inject(OrganizationService);
  private snackBar = inject(SnackbarService);

  form = new FormGroup({
    organization: new FormControl<IOrganization | string | null>(null),
    startDate: new FormControl(DateTime.local().startOf('month'), {
      nonNullable: true,
    }),
    endDate: new FormControl(DateTime.local().endOf('month'), {
      nonNullable: true,
    }),
  });

  // TODO: migrar de Reactive Forms para Signal Forms (Angular 21+) quando a API não for mais experimental
  organization = toSignal(this.form.controls.organization.valueChanges);
  startDate = toSignal(this.form.controls.startDate.valueChanges, {
    initialValue: this.form.controls.startDate.defaultValue,
  });
  endDate = toSignal(this.form.controls.endDate.valueChanges, {
    initialValue: this.form.controls.endDate.defaultValue,
  });

  filteredOrganizations = toSignal(
    this.form.controls.organization.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((value) =>
        this.organizationService.searchOrganizations(
          typeof value === 'string' ? value : (value?.name ?? ''),
        ),
      ),
    ),
    { initialValue: [] },
  );

  viewMode = computed(() => {
    const org = this.organization();
    return org && typeof org !== 'string' && org.id
      ? 'organization'
      : 'general';
  });

  formattedReferenceDate = computed(() => {
    const start = this.startDate();
    const end = this.endDate();
    if (!start || !end) return '';
    return `${start.toLocaleString()} - ${end.toLocaleString()}`;
  });

  generalSummary = resource({
    params: () => ({
      view: this.viewMode(),
      startDate: this.startDate(),
      endDate: this.endDate(),
    }),
    loader: async ({ params, abortSignal }) => {
      if (params.view !== 'general' || !params.startDate || !params.endDate)
        return null;

      const { data } = await this.orpc.safeClient.admin.internalContractSummary(
        {
          startDate: params.startDate.toISODate(),
          endDate: params.endDate.toISODate(),
        },
        { signal: abortSignal },
      );
      return data;
    },
  });

  organizationSummary = resource({
    params: () => ({
      view: this.viewMode(),
      organization: this.organization(),
      startDate: this.startDate(),
      endDate: this.endDate(),
    }),
    loader: async ({ params, abortSignal }) => {
      const org = params.organization;
      if (
        params.view !== 'organization' ||
        !params.startDate ||
        !params.endDate ||
        !org ||
        typeof org === 'string'
      ) {
        return null;
      }
      const { data, error } =
        await this.orpc.safeClient.admin.internalContractSummary(
          {
            organizationId: org.id!,
            startDate: params.startDate.toISODate(),
            endDate: params.endDate.toISODate(),
          },
          { signal: abortSignal },
        );
      if (error) {
        throw error;
      }
      return data;
    },
  });

  generalDataSource = computed<SummaryRow[]>(() => {
    const summary = this.generalSummary.value();
    if (!summary) return [];
    return this.mapSummaryToDatasource(summary, 'general');
  });

  displayedColumns = ['indicator', 'referenceDate', 'value'];

  organizationDataSource = computed<SummaryRow[]>(() => {
    const summary = this.organizationSummary.value();
    const org = this.organization();
    if (!summary || !org) return [];

    return this.mapSummaryToDatasource(summary, 'organization');
  });

  private deSerializeInfinity(value: number): number {
    return value === -1 ? Number.POSITIVE_INFINITY : value;
  }

  private mapSummaryToDatasource(
    summary: contractOutput['admin']['internalContractSummary'],
    viewMode: 'general' | 'organization',
  ): SummaryRow[] {
    const mappedSummary: SummaryRow[] = [
      viewMode === 'general'
        ? {
            indicator: 'Total de Clientes Cadastrados',
            value: summary.totalOrgs,
          }
        : {
            indicator: 'Cliente',
            value: `${summary.organizationName} (${formatCNPJ(summary.organizationCnpj ?? '')})`,
          },
      {
        indicator:
          viewMode === 'general'
            ? 'Total de Contratos Ativos'
            : 'Contratos Ativos',
        value: summary.activeContracts,
      },
      {
        indicator:
          viewMode === 'general'
            ? 'Total de Contratos Inativos'
            : 'Contratos Inativos',
        value: summary.inactiveContracts,
      },

      {
        indicator: 'Compliance Socioambiental - Contratados',
        value: this.deSerializeInfinity(
          summary.compliance.contractedOrInfinity,
        ),
        unit: 'relatórios',
      },
      {
        indicator: 'Compliance Socioambiental - Emitidos',
        value: summary.compliance.emitted,
        unit: 'relatórios',
      },
      {
        indicator: 'Histórico de Safras - Contratados',
        value: this.deSerializeInfinity(summary.history.contractedOrInfinity),
        unit: 'relatórios',
      },
      {
        indicator: 'Histórico de Safras - Emitidos',
        value: summary.history.emitted,
        unit: 'relatórios',
      },
      {
        indicator: 'Monitoramento da Safra - Hectares Contratados',
        value: this.deSerializeInfinity(
          summary.monitoring.contractedHectaresOrInfinity,
        ),
        unit: 'ha',
      },
      {
        indicator: 'Monitoramento da Safra - Hectares Solicitados',
        value: summary.monitoring.requestedHectares,
        unit: 'ha',
      },
    ];
    return mappedSummary;
  }

  displayOrganization(org: IOrganization | null): string {
    return org?.name ?? '';
  }

  clearOrganization() {
    this.form.controls.organization.setValue(null);
  }

  formatValue(
    value: number | string | undefined | null,
    unit?: string,
  ): string {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'string') return value;
    const formatted = value.toLocaleString('pt-BR');
    return unit ? `${formatted} ${unit}` : formatted;
  }
}
